import { useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type { Subscription } from 'relay-runtime';

import { useAmpli } from '@/ampli';
import type { CompareAddressLookupFailedProperties } from '@/ampli/generated';

import type { useCompareAddressAutocompleteQuery } from './__generated__/useCompareAddressAutocompleteQuery.graphql';

type CompareAddressSuggestion = {
  id: string;
  label: string;
};

type SearchLookupFailureReason = Exclude<
  CompareAddressLookupFailedProperties['reason'],
  'submitted_without_selection'
>;

type CompareAddressAutocompleteOptions = {
  placement: CompareAddressLookupFailedProperties['placement'];
  blogSlug?: string;
};

const minQueryLength = 3;
const searchDebounceMs = 200;
const noMatchesSettleMs = 1_500;

const useCompareAddressAutocomplete = ({
  placement,
  blogSlug,
}: CompareAddressAutocompleteOptions) => {
  const environment = useRelayEnvironment();
  const ampli = useAmpli();
  const [suggestions, setSuggestions] = useState<
    readonly CompareAddressSuggestion[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Subscription | null>(null);
  const noMatchesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedSearchStartRef = useRef(false);
  const hasTrackedLookupErrorRef = useRef(false);
  const lastNoMatchesQueryRef = useRef<string | null>(null);

  const cancelPendingSearch = () => {
    if (pendingTimerRef.current != null) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    if (noMatchesTimerRef.current != null) {
      clearTimeout(noMatchesTimerRef.current);
      noMatchesTimerRef.current = null;
    }
    inFlightRef.current?.unsubscribe();
    inFlightRef.current = null;
  };

  useEffect(() => cancelPendingSearch, []);

  const trackSearchStart = (query: string) => {
    if (hasTrackedSearchStartRef.current || query.trim().length === 0) {
      return;
    }

    hasTrackedSearchStartRef.current = true;
    ampli((client) =>
      client.compareAddressSearchStarted({ placement, blog_slug: blogSlug }),
    );
  };

  const trackLookupFailure = (reason: SearchLookupFailureReason) => {
    ampli((client) =>
      client.compareAddressLookupFailed({
        placement,
        blog_slug: blogSlug,
        reason,
      }),
    );
  };

  const trackLookupError = () => {
    if (hasTrackedLookupErrorRef.current) {
      return;
    }

    hasTrackedLookupErrorRef.current = true;
    trackLookupFailure('lookup_error');
  };

  /** Only counts empty results the visitor stops on, not partial queries mid-typing. */
  const scheduleNoMatchesTracking = (query: string) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (lastNoMatchesQueryRef.current === normalizedQuery) {
      return;
    }

    noMatchesTimerRef.current = setTimeout(() => {
      noMatchesTimerRef.current = null;
      lastNoMatchesQueryRef.current = normalizedQuery;
      trackLookupFailure('no_matches');
    }, noMatchesSettleMs);
  };

  const search = (query: string) => {
    cancelPendingSearch();
    setHasError(false);
    trackSearchStart(query);

    if (query.trim().length < minQueryLength) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    pendingTimerRef.current = setTimeout(() => {
      inFlightRef.current = fetchQuery<useCompareAddressAutocompleteQuery>(
        environment,
        graphql`
          query useCompareAddressAutocompleteQuery($query: String!) {
            landlordAutocompleteAddresses(query: $query) {
              id
              label
            }
          }
        `,
        { query },
      ).subscribe({
        next: (data) => {
          const nextSuggestions = (
            data.landlordAutocompleteAddresses ?? []
          ).flatMap(({ id, label }) => (label ? [{ id, label }] : []));
          setSuggestions(nextSuggestions);
          if (nextSuggestions.length === 0) {
            scheduleNoMatchesTracking(query);
          }
        },
        error: (error: unknown) => {
          console.error(error);
          setSuggestions([]);
          setHasError(true);
          setIsSearching(false);
          trackLookupError();
        },
        complete: () => setIsSearching(false),
      });
    }, searchDebounceMs);
  };

  return { suggestions, isSearching, hasError, minQueryLength, search };
};

export default useCompareAddressAutocomplete;
